import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import Audit5SIcon from 'src/components/ui-icons/menu/myTasks/5sAudit';
import Loading from 'src/components/Loading';
import FilterForPlanAction from 'src/pages/DashboardPage/vpo/components/planAction/filterForPlanAction/FilterForPlanAction';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectModelData } from '../../store/filtersData/selector';
import { fetchData } from '../../store/filtersData/actions';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import { selectCurrentUser } from 'src/modules/users/selector';
import { FavoriteButtonFilters } from 'src/modules/favoriteFilters/mt/FavoriteButtonFilters';
import { FavoriteFiltersList } from 'src/modules/favoriteFilters/mt/FavoriteFiltersList';
import { filterConfigFilter5SAudit, filtersOpenFilter5SAudit, initialFilter5SAudit, mockTypeAudit } from './dataFilterReport';
import ReportDoneTable from './ReportDoneTable';
import ExportToExcelReportDone from './ExportToExcelReportDone';
import { loadReportDone } from '../../store/reportDone5SAudit/actions';
import { selectReportDoneData, selectReportDoneLoading } from '../../store/reportDone5SAudit/selector';

const FAVORITE_FILTER_TYPE_5S_REPORT_DONE = 'reportDone5s';

const FAVORITE_FIELDS_MAP_5S_REPORT_DONE = [
  { key: 'location', title: 'Локация' },
  { key: 'department', title: 'Подразделение' },
  { key: 'site', title: 'Участок' },
  { key: 'area', title: 'Зона' },
  { key: 'typeAudit', title: 'Тип аудита' },
  { key: 'auditor', title: 'Аудитор' },
  { key: 'coAuditor', title: 'Соаудитор' },
];

export default function ReportDone5SAudit() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);

  const [filters, setFilters] = useState<any>(initialFilter5SAudit);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [filtersVersion, setFiltersVersion] = useState(0);
  const [reRenderFilterSettings, setReRenderFilterSettings] = useState(false);
  const [defaultUserFilter, setDefaultUserFilter] = useState<any>(null);
  const [userFilterCheck, setUserFilterCheck] = useState(false);
  const [reRenderUserFilter, setReRenderUserFilter] = useState<any>(null);
  const initialFiltersRef = useRef<any>(null);
  const lastFetchKey = useRef<string>('');

  const FilterLocation = useAppSelector((state: any) => selectModelData(state, 'mt-locations'));
  const dependsInner = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-depends'));
  const FilterDepartment = useAppSelector((state: any) => selectModelData(state, 'mt-departments'));
  const FilterArea = useAppSelector((state: any) => selectModelData(state, 'mt-areas'));

  const reportData = useAppSelector(selectReportDoneData);
  const reportLoading = useAppSelector(selectReportDoneLoading);

  useEffect(() => {
    dispatch(fetchData('mt-locations', {}, { page: 1, pageSize: 100 }));
    dispatch(fetchData('mt-departments', {}, { page: 1, pageSize: 100 }));
    dispatch(fetchData('mt-areas', {}, { page: 1, pageSize: 200 }));
    dispatch(fetchData('mt-5-s-depends', {}, { page: 1, pageSize: 100000 }));
  }, [dispatch]);

  //! локация по группе доступа
  useEffect(() => {
    if (filtersInitialized || !user || FilterLocation.length === 0 || FilterDepartment.length === 0 || dependsInner.length === 0) return;

    const ids: number[] = [];
    if (Array.isArray(user.isLocationAdminMyTasks)) ids.push(...user.isLocationAdminMyTasks);
    if (Array.isArray(user.isLocationEditorMyTasks)) ids.push(...user.isLocationEditorMyTasks);
    const defaultIds = [...new Set(ids)];

    if (defaultIds.length > 0) {
      const defaultLocations = FilterLocation.filter((loc: any) => defaultIds.includes(loc.id));
      if (defaultLocations.length > 0) {
        const next: any = {
          ...initialFilter5SAudit,
          location: {
            selected: defaultLocations.map((loc: any) => loc.Title),
            selectedIds: defaultLocations.map((loc: any) => loc.id),
            search: '',
          },
        };

        //! отдел по группам доступа, и только если он есть у выбранных локаций в depends
        const defaultLocIds = defaultLocations.map((loc: any) => loc.id);
        const availableDeptIds = new Set(
          dependsInner
            .filter((dep: any) => defaultLocIds.includes(dep.Location?.id))
            .map((dep: any) => dep.Department?.id)
            .filter(Boolean),
        );
        const validDepts = FilterDepartment.filter((d: any) =>
          user.department && d.User_Department && d.User_Department.indexOf(user.department) !== -1 && availableDeptIds.has(d.id),
        );
        if (validDepts.length > 0) {
          next.department = {
            selected: validDepts.map((d: any) => d.Title),
            selectedIds: validDepts.map((d: any) => d.id),
            search: '',
          };
        }

        initialFiltersRef.current = next;
        setFilters(next);
        setFiltersInitialized(true);
        return;
      }
    }

    initialFiltersRef.current = initialFilter5SAudit;
    setFiltersInitialized(true);
  }, [user, FilterLocation, FilterDepartment, dependsInner, filtersInitialized]);

  //! фильтрация на основе mt-5-s-depends
  const filteredOptions = useMemo(() => {
    const selectedLocationIds = filters.location?.selectedIds || [];
    const selectedDepartmentIds = filters.department?.selectedIds || [];
    const selectedAreaIds = filters.site?.selectedIds || [];

    let filteredDeps = dependsInner;

    if (selectedLocationIds.length > 0) {
      filteredDeps = filteredDeps.filter((dep: any) => selectedLocationIds.includes(dep.Location?.id));
    }

    const availableDeptIds = new Set(filteredDeps.map((dep: any) => dep.Department?.id).filter(Boolean));
    const departments = selectedLocationIds.length > 0 ? FilterDepartment.filter((d: any) => availableDeptIds.has(d.id)) : FilterDepartment;

    if (selectedDepartmentIds.length > 0) {
      filteredDeps = filteredDeps.filter((dep: any) => selectedDepartmentIds.includes(dep.Department?.id));
    }

    const availableAreaIds = new Set(filteredDeps.map((dep: any) => dep.Area?.id).filter(Boolean));
    const areas = selectedLocationIds.length > 0 || selectedDepartmentIds.length > 0 ? FilterArea.filter((a: any) => availableAreaIds.has(a.id)) : FilterArea;

    if (selectedAreaIds.length > 0) {
      filteredDeps = filteredDeps.filter((dep: any) => selectedAreaIds.includes(dep.Area?.id));
    }

    const availableZone5S = new Set(filteredDeps.map((dep: any) => dep.Zone).filter(Boolean));
    const allZone5S = [...new Set(dependsInner.map((dep: any) => dep.Zone).filter(Boolean))];
    const hasActiveFilters = selectedLocationIds.length > 0 || selectedDepartmentIds.length > 0 || selectedAreaIds.length > 0;
    const zones = hasActiveFilters ? [...availableZone5S].map((name) => ({ id: name, Title: name })) : allZone5S.map((name) => ({ id: name, Title: name }));

    return { departments, areas, zones };
  }, [filters, dependsInner, FilterDepartment, FilterArea]);

  useEffect(() => {
    if (!filtersInitialized) return;
    if (!filters.created?.startDate || !filters.created?.endDate) return;

    const typeAuditSelected = filters.typeAudit?.selected || [];
    const apiFilters = {
      startDate: filters.created.startDate,
      endDate: filters.created.endDate,
      locationIds: filters.location?.selectedIds || [],
      departmentIds: filters.department?.selectedIds || [],
      areaIds: filters.site?.selectedIds || [],
      zones: filters.area?.selected || [],
      typeAudit: typeAuditSelected.length === 1 ? typeAuditSelected[0] : null,
      auditorIds: filters.auditor?.selectedIds || [],
      coAuditorIds: filters.coAuditor?.selectedIds || [],
    };

    const key = JSON.stringify(apiFilters);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(loadReportDone(apiFilters));
  }, [filtersInitialized, filters, dispatch]);

  const filterConfigs = useMemo(() => ({
    created: {},
    location: { items: FilterLocation },
    department: { items: filteredOptions.departments },
    site: { items: filteredOptions.areas },
    area: { items: filteredOptions.zones },
    typeAudit: { items: mockTypeAudit },
  }), [FilterLocation, filteredOptions]);

  const filtersMap = useMemo(() => ({
    created: [],
    location: FilterLocation,
    department: filteredOptions.departments,
    site: filteredOptions.areas,
    area: filteredOptions.zones,
    typeAudit: mockTypeAudit,
  }), [FilterLocation, filteredOptions]);

  const debouncedSetFilters = useMemo(
    () => debounce((next: any) => setFilters(next), 400),
    [],
  );

  const handleFiltersChange = useCallback(
    (next: any) => {
      debouncedSetFilters(next);
    },
    [debouncedSetFilters],
  );

  //! Избранные фильтры
  const favoriteFiltersViews = useMemo(() => {
    const mt: Record<string, any> = {};
    const display: Record<string, any> = {};
    Object.keys(filters || {}).forEach((key) => {
      if (key === 'created') return;
      const f: any = (filters as any)[key];
      if (f && Array.isArray(f.selectedIds) && f.selectedIds.length > 0) {
        mt[key] = f.selectedIds;
        display[key] = Array.isArray(f.selected) && f.selected.length > 0 ? f.selected : f.selectedIds;
      }
    });
    const filtersWithoutPeriod = { ...filters };
    delete filtersWithoutPeriod.created;
    return {
      filtersStr: Object.keys(mt).length > 0 ? JSON.stringify(mt) : '',
      displayFiltersStr: Object.keys(display).length > 0 ? JSON.stringify(display) : '',
      filtersParamsStr: Object.keys(mt).length > 0 ? JSON.stringify(filtersWithoutPeriod) : '',
    };
  }, [filters]);

  const handleApplyFavorite = useCallback((parsedFullFilters: any) => {
    if (!parsedFullFilters || Object.keys(parsedFullFilters).length === 0) return;
    const merged = {
      ...initialFilter5SAudit,
      ...parsedFullFilters,
      created: filters.created || initialFilter5SAudit.created,
    };
    initialFiltersRef.current = merged;
    setFilters(merged);
    setFiltersVersion((v) => v + 1);
  }, [filters.created]);

  return (
    <div className='flex justify-between h-[calc(100vh-2px)] overflow-hidden'>
      <div className='w-[320px] mr-2 bg-white flex-shrink-0'>
        <FilterForPlanAction
          key={`filter-${filtersVersion}-${initialFiltersRef.current?.location?.selectedIds?.[0] || 'default'}`}
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={initialFiltersRef.current || initialFilter5SAudit}
          filtersOpen={filtersOpenFilter5SAudit}
          filtersConfig={filterConfigFilter5SAudit}
          responsibleFil={false}
          selectedGropu={true}
          heightFilter={'h-[calc(100vh-2px)]'}
        />
      </div>
      <div className='flex-1 min-w-0 p-4 flex flex-col h-[calc(100vh-2px)] overflow-auto'>
        <div className='flex items-center justify-between mb-4 flex-shrink-0'>
          <div className='flex items-center text-gray-600'>
            <span className='pr-3'>
              <ReportIcon />
            </span>
            <span className='cursor-pointer text-base' onClick={() => navigate('/dashboard/3/reports')}>
              Отчеты
            </span>
            <span className='mx-2'>→</span>
            <span className='flex items-center gap-2 text-base text-primary'>
              <Audit5SIcon />
              Выполнение графика аудитов 5S
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <ExportToExcelReportDone months={reportData?.months || []} />
            <FavoriteButtonFilters
              filters={favoriteFiltersViews.filtersStr}
              displayFilters={favoriteFiltersViews.displayFiltersStr}
              reRenderFilterSettings={reRenderFilterSettings}
              setReRenderFilterSettings={setReRenderFilterSettings}
              filtersParams={favoriteFiltersViews.filtersParamsStr}
              type={FAVORITE_FILTER_TYPE_5S_REPORT_DONE}
            />
            <FavoriteFiltersList
              reRenderTableFlag={() => { }}
              reRenderFilterSettings={reRenderFilterSettings}
              setReRenderFilterSettings={setReRenderFilterSettings}
              setFiltersParams={handleApplyFavorite}
              type={FAVORITE_FILTER_TYPE_5S_REPORT_DONE}
              filtersParams={favoriteFiltersViews.filtersParamsStr}
              fieldsMap={FAVORITE_FIELDS_MAP_5S_REPORT_DONE}
              setDefaultUserFilter={setDefaultUserFilter}
              setUserFilterCheck={setUserFilterCheck}
              userFilterCheck={userFilterCheck}
              setReRenderUserFilter={setReRenderUserFilter}
            />
          </div>
        </div>

        <div className='rounded-lg bg-white p-4 shadow-md'>
          {reportLoading && (
            <div className='flex items-center justify-center h-64'>
              <Loading />
            </div>
          )}
          {!reportLoading && reportData && (
            <ReportDoneTable months={reportData.months} percentColors={reportData.percentColors} />
          )}
        </div>
      </div>
    </div>
  );
}
